import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Clock, CheckCircle, Truck, XCircle, MapPin, CreditCard, Banknote, X, Phone, MessageCircle, Search, ShoppingBag } from 'lucide-react';
import Navbar from '../components/Navbar';
import API from '../utils/api';
import { toast } from 'react-toastify';
import { EmptyState, ErrorState, LoadingState } from '../components/StateMessage';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Clock, color: '#F59E0B', bg: '#FFFBEB' },
  confirmed: { label: 'Confirmed', icon: CheckCircle, color: '#3B82F6', bg: '#EFF6FF' },
  processing: { label: 'Processing', icon: Package, color: '#8B5CF6', bg: '#F5F3FF' },
  out_for_delivery: { label: 'Out for Delivery', icon: Truck, color: 'var(--orange)', bg: 'var(--orange-soft)' },
  delivered: { label: 'Delivered', icon: CheckCircle, color: 'var(--green)', bg: '#ECFDF5' },
  cancelled: { label: 'Cancelled', icon: XCircle, color: 'var(--red)', bg: '#FEF2F2' },
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const { replaceCart } = useCart();
  const { user } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();

  const fetchOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await API.get('/orders/my-orders');
      setOrders(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load your orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const cancelOrder = async (orderId) => {
    if (!window.confirm('Cancel this order?')) return;
    try {
      const res = await API.put(`/orders/${orderId}/cancel`);
      setOrders(prev => prev.map(o => o._id === orderId ? res.data : o));
      toast.success('Order cancelled');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel order');
    }
  };

  const reorder = async (orderId) => {
    try {
      const res = await API.get(`/orders/${orderId}/reorder`);
      const items = (res.data.items || []).map(item => ({
        key: item.variant ? `${item.material?._id || item.material}-${item.variant}` : (item.material?._id || item.material),
        material: item.material,
        owner: res.data.owner,
        quantity: item.quantity,
        variant: item.variant ? { size: item.variant, price: item.price, unit: item.unit } : null,
        price: item.price,
        name: item.name,
        category: item.category,
        unit: item.unit
      }));
      replaceCart(items, res.data.owner);
      toast.success('Previous order added to cart');
      if (res.data.skipped?.length) toast.warning(`Unavailable items skipped: ${res.data.skipped.join(', ')}`);
      navigate('/cart');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unable to reorder');
    }
  };

  const payPlatformFee = async (order) => {
    try {
      const payRes = await API.post('/payment/create-order', { orderId: order._id, amount: order.advanceAmount || order.platformFee || 0 });
      const { orderId: rzpOrderId, key, testMode, amount: rzpAmount, merchantName } = payRes.data;

      if (testMode || !key || key === 'rzp_test_placeholder') {
        await API.post('/payment/confirm-test', { orderId: order._id });
        toast.success('Platform fee paid. Order confirmed.');
        fetchOrders();
        return;
      }

      if (!window.Razorpay) {
        toast.error('Razorpay not loaded. Please refresh.');
        return;
      }

      new window.Razorpay({
        key,
        amount: rzpAmount,
        currency: 'INR',
        name: merchantName || 'Material Stock Admin',
        description: `Platform Fee #${order._id.slice(-6).toUpperCase()}`,
        order_id: rzpOrderId,
        handler: async (response) => {
          try {
            await API.post('/payment/verify', {
              orderId: order._id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpaySignature: response.razorpay_signature
            });
            toast.success('Platform fee paid. Order confirmed.');
            fetchOrders();
          } catch {
            toast.error('Payment verification failed');
          }
        },
        prefill: { name: user?.name, email: user?.email, contact: user?.phone },
        theme: { color: getComputedStyle(document.documentElement).getPropertyValue('--orange').trim() || '#15803D' },
        modal: { ondismiss: () => toast.warning('Payment cancelled. Order remains pending.') }
      }).open();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unable to start payment');
    }
  };

  const matchesOrderSearch = (order) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const haystack = [
      order._id,
      order._id?.slice(-8),
      order.owner?.shopName,
      order.owner?.user?.name,
      order.owner?.user?.phone,
      order.status,
      order.deliveryAddress?.city,
      order.deliveryAddress?.state,
      order.totalAmount,
      order.itemsAmount,
      ...(order.items || []).flatMap(item => [item.name, item.category, item.variant, item.unit]),
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(q);
  };

  const filtered = (filter === 'all' ? orders : orders.filter(o => o.status === filter)).filter(matchesOrderSearch);

  return (
    <div style={{ minHeight: "100vh", background: "var(--page-bg)" }} className="page-with-bottom-nav">
      <Navbar />
      <div className="customer-orders-shell" style={{ maxWidth: 900, margin: '0 auto', padding: '28px 20px' }}>
        <div className="customer-orders-heading">
          <div className="customer-orders-title-icon"><ShoppingBag size={21} /></div>
          <div>
            <span>Order history</span>
            <h1>My Orders</h1>
            <p>{orders.length} {orders.length === 1 ? 'order' : 'orders'} across all suppliers</p>
          </div>
        </div>

        <div className="customer-order-search" style={{ position: 'relative', marginBottom: 14 }}>
          <Search size={17} />
          <input
            className="form-input"
            placeholder="Search order number, shop name, item, city, status..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 14, paddingRight: search ? 42 : 14, background: 'white' }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 28, height: 28, borderRadius: 8, background: 'var(--gray-light)', color: 'var(--gray)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <X size={14} />
            </button>
          )}
        </div>

        {/* Filter */}
        <div className="customer-order-filters" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          {['all', ...Object.keys(STATUS_CONFIG)].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              style={{ padding: '7px 16px', borderRadius: 20, border: `1.5px solid ${filter === s ? 'var(--orange)' : 'var(--gray-border)'}`, background: filter === s ? 'var(--orange-soft)' : 'white', color: filter === s ? 'var(--orange)' : 'var(--dark)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
              {s === 'all' ? 'All Orders' : STATUS_CONFIG[s].label}
            </button>
          ))}
        </div>

        {loading ? (
          <LoadingState label="Loading your orders..." />
        ) : error ? (
          <ErrorState message={error} onRetry={fetchOrders} />
        ) : filtered.length === 0 ? (
          <EmptyState title="No orders found" message="Your orders will appear here after checkout." />
        ) : (
          <div className="customer-order-list" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {filtered.map(order => <OrderCard key={order._id} order={order} onCancel={cancelOrder} onReorder={reorder} onPayPlatformFee={payPlatformFee} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function OrderCard({ order, onCancel, onReorder, onPayPlatformFee }) {
  const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const StatusIcon = status.icon;
  const canCancel = ['pending', 'confirmed'].includes(order.status) && order.paymentStatus?.advance !== 'paid';
  const itemsAmount = order.itemsAmount ?? order.items?.reduce((sum, item) => sum + (Number(item.price) * Number(item.quantity)), 0) ?? 0;
  const platformFee = order.platformFee || order.advanceAmount || 0;
  const advancePending = order.paymentStatus?.advance !== 'paid';
  const ownerPhone = order.owner?.user?.phone || order.owner?.phone || '';
  const cleanOwnerPhone = String(ownerPhone || '').replace(/\D/g, '');
  const orderNo = order._id.slice(-8).toUpperCase();
  const formatRs = (value) => `Rs ${Number(value || 0).toLocaleString('en-IN')}`;
  const orderItemsText = (order.items || []).map((item, index) => {
    const itemTotal = Number(item.price || 0) * Number(item.quantity || 0);
    const variant = item.variant ? ` (${item.variant})` : '';
    return `${index + 1}. ${item.name}${variant} - ${item.quantity} ${item.unit || ''} x ${formatRs(item.price)} = ${formatRs(itemTotal)}`;
  }).join('\n');
  const deliveryAddressText = [
    order.deliveryAddress?.street || order.deliveryAddress?.address,
    order.deliveryAddress?.city,
    order.deliveryAddress?.state,
    order.deliveryAddress?.pincode,
  ].filter(Boolean).join(', ');
  const chatText = encodeURIComponent([
    `Hello ${order.owner?.shopName || 'Shop'},`,
    '',
    'I am contacting you from Material Stock about my order. Please check the details below.',
    '',
    `Order ID: #${orderNo}`,
    `Order Status: ${status.label}`,
    `Order Date: ${new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`,
    '',
    'Items:',
    orderItemsText || 'No items found',
    '',
    'Payment Summary:',
    `Materials Amount: ${formatRs(itemsAmount)}`,
    `Platform / Advance Fee: ${formatRs(platformFee)}`,
    `Total Amount: ${formatRs(order.totalAmount)}`,
    `Paid Online: ${formatRs(order.advanceAmount)} (${order.paymentStatus?.advance === 'paid' ? 'Paid' : 'Pending'})`,
    `Materials Balance: ${formatRs(order.remainingAmount)} (${order.paymentStatus?.remaining === 'paid' ? 'Paid' : 'Pending'})`,
    '',
    'Delivery Address:',
    deliveryAddressText || 'Address not available',
    '',
    'Please confirm item availability, delivery time, and any order updates.',
  ].join('\n'));
  const whatsappPhone = cleanOwnerPhone.length === 10 ? `91${cleanOwnerPhone}` : cleanOwnerPhone;
  const printInvoice = async () => {
    try {
      const res = await API.get(`/orders/${order._id}/invoice`);
      const invoice = res.data;
      const logoUrl = invoice.logoImage
        ? (invoice.logoImage.startsWith('http') ? invoice.logoImage : window.location.origin + (invoice.logoImage.startsWith('/') ? '' : '/') + invoice.logoImage)
        : (window.location.origin + '/material-stock-logo.jpg');
      const supportEmail = invoice.supportEmail || branding.supportEmail || 'support@materialstock.in';
      const win = window.open('', '_blank');
      if (!win) return toast.error('Please allow popup to print invoice');
      win.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
          <style>
            body{font-family:'Helvetica Neue',Arial,sans-serif;padding:32px;color:#1f2937;max-width:800px;margin:auto;background:#fff}
            .header{display:flex;justify-content:space-between;align-items:center;border-bottom:2px solid #22c55e;padding-bottom:18px;margin-bottom:24px}
            .brand-logo{height:52px;max-width:220px;object-fit:contain}
            .invoice-title{font-size:22px;font-weight:800;color:#166534;text-align:right}
            .details-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:24px}
            .box{background:#f9fafb;border:1px solid #e5e7eb;border-radius:10px;padding:16px}
            .box h4{margin:0 0 8px;color:#374151;font-size:13px;text-transform:uppercase;letter-spacing:0.5px}
            .box p{margin:3px 0;font-size:13px;color:#4b5563}
            table{width:100%;border-collapse:collapse;margin:20px 0}
            th{background:#f0fdf4;color:#166534;font-size:13px;font-weight:700;text-transform:uppercase;padding:12px;border:1px solid #bbf7d0;text-align:left}
            td{padding:12px;border:1px solid #e5e7eb;font-size:13px;color:#374151}
            .text-right{text-align:right}
            .summary-table{width:320px;margin-left:auto}
            .summary-table td{padding:8px 12px}
            .grand-total{background:#166534;color:#fff;font-size:15px;font-weight:800}
            .grand-total td{color:#fff;border:none}
            .badge{display:inline-block;padding:3px 8px;border-radius:12px;font-size:11px;font-weight:700;background:#dcfce7;color:#15803d}
            .footer{margin-top:36px;padding-top:16px;border-top:1px solid #e5e7eb;text-align:center;color:#9ca3af;font-size:12px}
            @media print { .no-print { display: none !important; } }
          </style>
        </head>
        <body>
          <div class="no-print" style="margin-bottom: 20px; display: flex; gap: 10px; align-items: center; justify-content: space-between; background: #f0fdf4; border: 1px solid #bbf7d0; padding: 12px 16px; border-radius: 10px; flex-wrap: wrap;">
            <button onclick="window.close()" style="display: inline-flex; align-items: center; gap: 6px; background: #ffffff; color: #15803d; border: 1.5px solid #16a34a; padding: 8px 16px; border-radius: 8px; font-weight: 700; cursor: pointer; font-size: 13px; font-family: inherit;">
              ← Back to Orders
            </button>
            <div style="display: flex; gap: 8px; flex-wrap: wrap;">
              <button onclick="downloadPdfInvoice()" style="display: inline-flex; align-items: center; gap: 6px; background: #ffffff; color: #16a34a; border: 1.5px solid #16a34a; padding: 8px 16px; border-radius: 8px; font-weight: 700; cursor: pointer; font-size: 13px; font-family: inherit;">
                📥 Download PDF
              </button>
              <button onclick="window.print()" style="display: inline-flex; align-items: center; gap: 6px; background: #16a34a; color: #ffffff; border: none; padding: 8px 18px; border-radius: 8px; font-weight: 700; cursor: pointer; font-size: 13px; font-family: inherit;">
                🖨️ Print Invoice
              </button>
            </div>
          </div>

          <div id="pdf-invoice-content" style="background:#ffffff;padding:8px">
            <div class="header">
              <div style="display:flex;align-items:center;gap:14px">
                <img src="${logoUrl}" alt="Material Stock" class="brand-logo" />
                <div>
                  <div style="font-size:20px;font-weight:800;color:#111827">Material Stock</div>
                  <div style="color:#6b7280;font-size:12px;margin-top:2px">Cyber Residency, Indira Nagar, Gachibowli, Hyderabad</div>
                  <div style="color:#6b7280;font-size:12px">Phone: +91 6300440316 | Email: ${supportEmail}</div>
                </div>
              </div>
              <div>
                <div class="invoice-title">TAX INVOICE</div>
                <div style="font-size:14px;font-weight:700;color:#166534;margin-top:4px">${invoice.invoiceNo}</div>
                <div style="font-size:12px;color:#6b7280;margin-top:2px">Date: ${new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
              </div>
            </div>

            <div class="details-grid">
              <div class="box">
                <h4>Billed To (Customer)</h4>
                <p><strong>${order.customer?.name || 'Customer'}</strong></p>
                <p>Phone: ${order.customer?.phone || '-'}</p>
                <p>Email: ${order.customer?.email || '-'}</p>
                <p>Address: ${deliveryAddressText || '-'}</p>
              </div>
              <div class="box">
                <h4>Supplier / Shop Owner</h4>
                <p><strong>${order.owner?.shopName || 'Shop'}</strong></p>
                <p>Phone: ${cleanOwnerPhone || '-'}</p>
                <p>Status: <span style="text-transform:capitalize;font-weight:700">${order.status}</span></p>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Qty</th>
                  <th class="text-right">Price</th>
                  <th class="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                ${(order.items || []).map(item => `
                  <tr>
                    <td><strong>${item.name}</strong> ${item.variant ? `(${item.variant})` : ''}</td>
                    <td>${item.quantity} ${item.unit || ''}</td>
                    <td class="text-right">₹${Number(item.price).toLocaleString('en-IN')}</td>
                    <td class="text-right">₹${Number(item.price * item.quantity).toLocaleString('en-IN')}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>

            <table class="summary-table">
              <tr>
                <td>Advance Paid Online:</td>
                <td class="text-right" style="color:#15803d;font-weight:700">₹${Number(order.advanceAmount || 0).toLocaleString('en-IN')} <span class="badge">PAID</span></td>
              </tr>
              <tr>
                <td>Remaining Balance:</td>
                <td class="text-right" style="color:#b45309;font-weight:700">₹${Number(order.remainingAmount || 0).toLocaleString('en-IN')}</td>
              </tr>
              <tr class="grand-total">
                <td><strong>Total Amount:</strong></td>
                <td class="text-right"><strong>₹${Number(order.totalAmount || 0).toLocaleString('en-IN')}</strong></td>
              </tr>
            </table>

            <div class="footer">
              <p>Thank you for ordering with Material Stock! For support, call +91 6300440316.</p>
              <p>This is a computer-generated tax invoice.</p>
            </div>
          </div>

          <script>
            function downloadPdfInvoice() {
              var element = document.getElementById('pdf-invoice-content');
              var opt = {
                margin: [0.3, 0.3, 0.3, 0.3],
                filename: '${invoice.invoiceNo}_Invoice.pdf',
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, logging: false },
                jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
              };
              if (window.html2pdf) {
                html2pdf().set(opt).from(element).save();
              } else {
                window.print();
              }
            }

            window.onload = function() {
              setTimeout(function() { downloadPdfInvoice(); }, 500);
            };
          </script>
        </body>
        </html>
        </html>
      `);
      win.document.close();
    } catch {
      toast.error('Unable to open invoice');
    }
  };

  return (
    <article className={`customer-order-card status-${order.status}`} style={{ background: 'white', borderRadius: 16, boxShadow: 'var(--shadow)', overflow: 'hidden' }}>
      {/* Header */}
      <div className="customer-order-card-head" style={{ padding: '14px 20px', background: status.bg, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <StatusIcon size={16} color={status.color} />
          <span style={{ fontWeight: 700, color: status.color, fontSize: 14 }}>{status.label}</span>
          <span style={{ color: 'var(--gray)', fontSize: 12 }}>· Order #{order._id.slice(-8).toUpperCase()}</span>
        </div>
        <span style={{ color: 'var(--gray)', fontSize: 12 }}>{new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
      </div>

      <div className="customer-order-card-body" style={{ padding: 20 }}>
        {/* Shop */}
        <div className="customer-order-supplier" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid var(--gray-border)', flexWrap: 'wrap' }}>
          <div style={{ width: 40, height: 40, background: 'linear-gradient(135deg,var(--orange),var(--amber))', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 16 }}>
            {order.owner?.shopName?.[0] || 'S'}
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{order.owner?.shopName || 'Shop'}</div>
            <div style={{ color: 'var(--gray)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={10} /> {order.deliveryAddress?.city}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <a href={ownerPhone ? `tel:${ownerPhone}` : undefined}
              onClick={(e) => { if (!ownerPhone) { e.preventDefault(); toast.error('Owner phone not available'); } }}
              title="Call owner"
              aria-label="Call owner"
              style={{ width: 36, height: 36, borderRadius: 10, background: '#ECFDF5', color: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid #A7F3D0' }}>
              <Phone size={16} />
            </a>
            <a href={whatsappPhone ? `https://wa.me/${whatsappPhone}?text=${chatText}` : undefined}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => { if (!whatsappPhone) { e.preventDefault(); toast.error('Owner phone not available'); } }}
              title="Chat with owner"
              aria-label="Chat with owner"
              style={{ width: 36, height: 36, borderRadius: 10, background: '#EFF6FF', color: '#1D4ED8', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid #BFDBFE' }}>
              <MessageCircle size={16} />
            </a>
          </div>
        </div>

        {/* Items */}
        <div className="customer-order-items" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {order.items?.map((item, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span>{item.name} {item.variant ? `(${item.variant})` : ''} × {item.quantity} {item.unit}</span>
              <span style={{ fontWeight: 600 }}>₹{(item.price * item.quantity).toLocaleString()}</span>
            </div>
          ))}
        </div>

        {/* Payment Summary */}
        <div className="customer-order-payment" style={{ background: 'var(--gray-light)', borderRadius: 10, padding: '12px 16px', display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 4 }}>Materials</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--dark)' }}>₹{itemsAmount?.toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 4 }}>Platform Fee</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--orange)' }}>₹{platformFee?.toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 4 }}>Total Amount</div>
            <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--dark)' }}>₹{order.totalAmount?.toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 4 }}>Paid Online</div>
            <div style={{ fontWeight: 700, color: order.paymentStatus?.advance === 'paid' ? 'var(--green)' : 'var(--orange)' }}>
              ₹{order.advanceAmount} {order.paymentStatus?.advance === 'paid' ? '✓' : '(Pending)'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 4 }}>Materials Balance</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 700, color: order.paymentStatus?.remaining === 'paid' ? 'var(--green)' : 'var(--dark)' }}>
                ₹{order.remainingAmount?.toLocaleString()} {order.paymentStatus?.remaining === 'paid' ? '✓ Paid' : ''}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {order.paymentMethod?.remaining === 'cash' ? <Banknote size={14} color="var(--gray)" /> : <CreditCard size={14} color="var(--gray)" />}
            <span style={{ fontSize: 12, color: 'var(--gray)' }}>{order.paymentMethod?.remaining === 'cash' ? 'Cash on delivery' : 'Online on delivery'}</span>
          </div>
        </div>

        {advancePending && order.status !== 'cancelled' && (
          <div className="customer-order-payment-alert" style={{ marginTop: 12, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '11px 13px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 13, color: '#92400E' }}>Platform fee pending</div>
              <div style={{ color: '#92400E', fontSize: 12 }}>Pay {formatRs(platformFee)} to confirm this order. Until then it stays pending.</div>
            </div>
            <button onClick={() => onPayPlatformFee(order)} className="btn-primary" style={{ padding: '9px 13px', fontSize: 12, justifyContent: 'center' }}>
              <CreditCard size={14} /> Pay Platform Fee
            </button>
          </div>
        )}

        <div className="customer-order-tracking" style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--gray)', textTransform: 'uppercase' }}>Order Tracking</div>
          {(order.statusHistory?.length ? order.statusHistory : [{ status: order.status, note: 'Current status', at: order.createdAt }]).map((step, i) => {
            const cfg = STATUS_CONFIG[step.status] || status;
            return (
              <div key={`${step.status}-${i}`} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: cfg.color, marginTop: 4, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{cfg.label || step.status}</div>
                  <div style={{ color: 'var(--gray)', fontSize: 11 }}>{step.note || ''} {step.at ? `· ${new Date(step.at).toLocaleString('en-IN')}` : ''}</div>
                </div>
              </div>
            );
          })}
        </div>

        {canCancel && (
          <button onClick={() => onCancel(order._id)}
            style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: '#FEF2F2', color: 'var(--red)', border: '1.5px solid #FECACA', borderRadius: 9, padding: '9px 13px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
            <X size={14} /> Cancel Order
          </button>
        )}
        <div className="customer-order-footer-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          <button onClick={printInvoice} className="btn-outline" style={{ padding: '8px 12px', fontSize: 12 }}>Invoice</button>
          <button onClick={() => onReorder(order._id)} className="btn-outline" style={{ padding: '8px 12px', fontSize: 12 }}>Reorder</button>
        </div>
      </div>
    </article>
  );
}
